/**
 * Onboarding API Utilities
 *
 * Provides authenticated API helpers for onboarding flow.
 * All API calls during onboarding require JWT Bearer token authentication.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Get auth token from localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Make an authenticated API request
 *
 * @param endpoint - API endpoint (e.g., '/api/onboarding/create-organization')
 * @param options - Fetch options (method, body, etc.)
 * @returns Response object
 * @throws Error if request fails or token is missing
 */
export const authenticatedFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found. Please log in again.');
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  return response;
};

/**
 * GET request with authentication
 */
export const apiGet = async <T = any>(endpoint: string): Promise<T> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * POST request with authentication
 */
export const apiPost = async <T = any>(
  endpoint: string,
  data?: Record<string, any>
): Promise<T> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * PUT request with authentication
 */
export const apiPut = async <T = any>(
  endpoint: string,
  data?: Record<string, any>
): Promise<T> => {
  const response = await authenticatedFetch(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};

// ============================================
// Onboarding-specific API functions
// ============================================

/**
 * Get current user data
 */
export const getCurrentUser = async () => {
  return apiGet('/api/users/me');
};

/**
 * Discover organizations by email domain
 */
export const discoverOrganizations = async (email: string) => {
  return apiGet(`/api/onboarding/discover-organizations?email=${encodeURIComponent(email)}`);
};

/**
 * Create a new organization
 */
export const createOrganization = async (data: {
  name: string;
  domain?: string | null;
  isDiscoverable?: boolean;
  selectedPlanSlug?: string | null;
}) => {
  return apiPost('/api/onboarding/create-organization', data);
};

/**
 * Join an organization directly
 */
export const joinOrganization = async (organizationId: number) => {
  return apiPost('/api/onboarding/join-organization', { organizationId });
};

/**
 * Complete user profile
 */
export const completeProfile = async (data: {
  firstName: string;
  lastName: string;
  title?: string | null;
  department?: string | null;
  profileImage?: string | null;
  timeZone: string;
  country?: string | null;
}) => {
  return apiPost('/api/onboarding/complete-profile', data);
};

/**
 * Send team invitations with language preference
 * The language is used to determine the language of the invitation email
 * and the onboarding flow the recipient will see
 *
 * @param emails - Array of email addresses to invite
 * @param language - Language code (e.g., 'en', 'ja')
 */
export const sendInvitationsWithLanguage = async (emails: string[], language: string) => {
  return apiPost('/api/onboarding/send-invitations', { emails, language });
};

/**
 * Setup user's default schedule and holiday preferences during onboarding
 *
 * @param data - Schedule data including weekly hours and holiday settings
 */
export const setupOnboardingSchedule = async (data: {
  schedule: {
    defaultWorkWeek: any;
    timeZone: string;
  };
  holidayCountry: string | null;
  observeHolidays: boolean;
}) => {
  return apiPost('/api/onboarding/setup-schedule', data);
};

/**
 * Save referral source (how user heard about SailLab)
 *
 * @param data - Referral data including channel and optional otherDetails
 */
export const saveReferralSource = async (data: {
  channel: string;
  otherDetails?: string;
}) => {
  return apiPost('/api/onboarding/save-referral-source', data);
};

/**
 * Mark onboarding as complete
 */
export const completeOnboarding = async () => {
  return apiPost('/api/onboarding/complete');
};

export interface CalendarIntegrationSummary {
  id: number;
  provider: string;
  connectionStatus: string;
  email?: string;
}

/**
 * List the user's CONNECTED calendar integrations.
 *
 * Shared by the wizard (to decide whether the calendar step is needed) and by
 * CalendarStep itself, so both judge "connected" by the same rule.
 */
export const getConnectedCalendars = async (): Promise<CalendarIntegrationSummary[]> => {
  const data = await apiGet('/api/multi-calendar/integrations');
  return (data.integrations || []).filter(
    (i: CalendarIntegrationSummary) => i.connectionStatus === 'connected'
  );
};

/**
 * Whether the user already has at least one connected calendar.
 */
export const hasConnectedCalendar = async (): Promise<boolean> => {
  const connected = await getConnectedCalendars();
  return connected.length > 0;
};
